import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import {
  getCapitalGlobalDocumentError,
  getCapitalGlobalDocumentLiveError,
} from '@/features/quotes/utils/capital-global-document-error.util';
import { formOptions } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useQuoteForm } from '../../../hooks/common/use-quote-form.hook';
import { useCaepfSelection } from '../../../hooks/common/use-caepf-selection.hook';
import { useInsurerCompanies } from '../../../hooks/common/use-insurer-companies.hook';
import { SelectCaepfModal } from '../../select-caepf.modal';
import { useCapitalGlobalReferenceData } from '../../../hooks/capital-global/use-capital-global-step1-reference-data.hook';
import { useCapitalGlobalStep1Prefill } from '../../../hooks/capital-global/use-capital-global-step1-prefill.hook';
import { useSubmitCapitalGlobalStep1 } from '../../../hooks/capital-global/use-submit-capital-global-step1.hook';
import { createSubStipulant, pickError } from './utils/step-helpers.util';
import type { CapitalGlobalQuoteStepOnNextResult } from '../capital-global-quote-step.types';
import type { DiretrixDocumentLookupState, SubStipulantEntry } from '../types';
import { CnaeField } from './sections/cnae-field.component';
import { DiretrixDocumentWatcher } from './sections/diretrix-document-watcher.component';
import { QuotationDataSection } from './sections/quotation-data-section.component';
import { SegmentSelect } from './sections/segment-select.component';
import { StipulantIdentification } from './sections/stipulant-identification.component';
import { SubStipulantsList } from './sections/sub-stipulants-list.component';

const formOpts = formOptions({
  defaultValues: {
    insuranceType: 'new' as 'new' | 'renewal',
    congenere: '',
    policyNumber: '',
    useIndividualDocument: false,
    documentNumber: '',
    cpf: '',
    name: '',
    businessSegment: '',
    businessCnae: '',
    averagePartnerAge: undefined as number | undefined,
    averageEmployeeAge: undefined as number | undefined,
    partnerCount: undefined as number | undefined,
    employeeCount: undefined as number | undefined,
    subStipulants: [] as SubStipulantEntry[],
  },
});

type Props = {
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  onNext: (createResult?: CapitalGlobalQuoteStepOnNextResult) => void;
  onBack: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

export function StepDadosDoSeguro({
  onNext,
  onBack,
  canGoNext = true,
  canGoBack = true,
  isViewMode = false,
}: Props) {
  const form = useQuoteForm({ ...formOpts });
  const [stipulantDocumentLookup, setStipulantDocumentLookup] =
    useState<DiretrixDocumentLookupState>({
      cleanDocument: '',
      status: 'idle',
    });
  const [subStipulantDocumentLookups, setSubStipulantDocumentLookups] =
    useState<Record<string, DiretrixDocumentLookupState>>({});
  const [stipulantCnaeSearch, setStipulantCnaeSearch] = useState('');
  const [stipulantSegmentLabel, setStipulantSegmentLabel] = useState('');
  const [stipulantCnaeLabel, setStipulantCnaeLabel] = useState('');

  const {
    handleSubmit,
    isPending,
    submitAttempted,
    subStipulantFieldErrors,
    setSubStipulantFieldErrors,
    resolvedPolicyId,
    hasPolicyId,
  } = useSubmitCapitalGlobalStep1({
    form,
    onNext,
    stipulantDocumentLookup,
    subStipulantDocumentLookups,
  });

  const { showProposalLoading } = useCapitalGlobalStep1Prefill({
    resolvedPolicyId,
    hasPolicyId,
    formStore: form.store,
    setFieldValue: form.setFieldValue,
  });

  const { data: insurerCompanies } = useInsurerCompanies();
  const stipulantUsesIndividualDocument = useStore(form.store, state =>
    Boolean(state.values.useIndividualDocument)
  );
  const stipulantBusinessCnae = useStore(form.store, state =>
    String(state.values.businessCnae ?? '')
  );
  const stipulantPrimaryActivityCode = !stipulantUsesIndividualDocument
    ? (stipulantDocumentLookup.primaryActivityCode ?? '').trim()
    : '';
  const {
    options: segmentOptions,
    isLoading: isSegmentOptionsLoading,
    isError: isSegmentOptionsError,
  } = useCapitalGlobalReferenceData({
    tableName: 'BusinessSegment',
    pageSize: 1000,
  });
  const {
    options: cnaeOptions,
    isLoading: isCnaeOptionsLoading,
    isError: isCnaeOptionsError,
  } = useCapitalGlobalReferenceData({
    tableName: 'CNAE',
    search: stipulantUsesIndividualDocument
      ? stipulantCnaeSearch
      : stipulantPrimaryActivityCode,
    pageSize: 50,
    enabled:
      stipulantUsesIndividualDocument || Boolean(stipulantPrimaryActivityCode),
  });

  const blockInteraction = isPending || showProposalLoading;
  const handleStipulantAverageAgesChange = useCallback(
    (ages: {
      averagePartnerAge?: number;
      averageEmployeeAge?: number;
      partnerCount?: number;
      employeeCount?: number;
    }) => {
      form.setFieldValue('averagePartnerAge', ages.averagePartnerAge);
      form.setFieldValue('averageEmployeeAge', ages.averageEmployeeAge);
      form.setFieldValue('partnerCount', ages.partnerCount);
      form.setFieldValue('employeeCount', ages.employeeCount);
    },
    [form]
  );

  // Modal de seleção de CAEPF: o CPF é só o ponto de entrada — o documento
  // efetivamente gravado é sempre a INSCRIÇÃO do CAEPF escolhido.
  const caepfSelection = useCaepfSelection({
    onSelect: (entry, consultedCpf) => {
      // O CAEPF sobrescreve o `documentNumber`; guardamos o CPF consultado à
      // parte (vazio quando o CAEPF foi digitado direto).
      form.setFieldValue('cpf', consultedCpf ?? '');
      if (entry.registrationNumber) {
        form.setFieldValue('documentNumber', entry.registrationNumber);
        void form.validateField('documentNumber', 'change');
      }
      if (entry.responsibleName) {
        form.setFieldValue('name', entry.responsibleName);
        void form.validateField('name', 'change');
      }
    },
  });
  const { requestSelection: requestCaepfSelection, resetFor: resetCaepfFor } =
    caepfSelection;

  const handleStipulantDocumentStatusChange = useCallback(
    (lookup: DiretrixDocumentLookupState) => {
      setStipulantDocumentLookup(lookup);

      if (lookup.status === 'found' && lookup.primaryActivityCode) {
        form.setFieldValue('businessCnae', lookup.primaryActivityCode.trim());
        void form.validateField('businessCnae', 'change');
      }

      if (lookup.status === 'found' && lookup.caepfItems?.length) {
        requestCaepfSelection(lookup.cleanDocument, lookup.caepfItems);
      }
    },
    [form, requestCaepfSelection]
  );

  useEffect(() => {
    if (stipulantUsesIndividualDocument) return;

    if (!stipulantPrimaryActivityCode) {
      if (stipulantCnaeLabel) setStipulantCnaeLabel('');
      return;
    }

    const cnaeOption =
      cnaeOptions.find(option => option.value === stipulantPrimaryActivityCode) ??
      cnaeOptions[0];

    if (stipulantBusinessCnae !== stipulantPrimaryActivityCode) {
      form.setFieldValue('businessCnae', stipulantPrimaryActivityCode);
      void form.validateField('businessCnae', 'change');
    }

    setStipulantCnaeLabel(
      cnaeOption?.label ?? stipulantPrimaryActivityCode
    );
  }, [
    cnaeOptions,
    form,
    stipulantBusinessCnae,
    stipulantCnaeLabel,
    stipulantPrimaryActivityCode,
    stipulantUsesIndividualDocument,
  ]);

  return (
    <div className='relative flex flex-col gap-8'>
      {showProposalLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando dados da cotação'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando dados da cotação...
          </Text>
        </div>
      ) : null}

      {/* Seção 1 — Dados da Cotação */}
      <form.AppField
        name='insuranceType'
        children={insuranceTypeField => (
          <form.AppField
            name='congenere'
            validators={{
              onChange: ({ value, fieldApi }) => {
                // Congênere só existe na Transferência de Congênere; fora dela o
                // campo nem é renderizado e não pode bloquear o avanço.
                const isRenewal =
                  fieldApi.form.getFieldValue('insuranceType') === 'renewal';
                if (!isRenewal) return undefined;
                return value.trim() ? undefined : 'Congênere é obrigatório';
              },
            }}
            children={congenereField => (
              <form.AppField
                name='policyNumber'
                children={policyNumberField => (
                  <QuotationDataSection
                    insuranceType={insuranceTypeField.state.value}
                    onInsuranceTypeChange={value => {
                      insuranceTypeField.handleChange(value);
                      // Ao voltar para Seguro Novo, limpa valor e erro dos campos
                      // de congênere (mesmo comportamento do Residencial).
                      if (value !== 'renewal') {
                        form.resetField('congenere');
                        form.resetField('policyNumber');
                      }
                    }}
                    congenereOptions={
                      insurerCompanies?.map(c => ({
                        value: String(c.id),
                        label: c.name,
                      })) ?? []
                    }
                    congenere={congenereField.state.value}
                    onCongenereChange={congenereField.handleChange}
                    congenereError={pickError(
                      congenereField.state.meta,
                      submitAttempted
                    )}
                    policyNumber={policyNumberField.state.value}
                    onPolicyNumberChange={policyNumberField.handleChange}
                    policyNumberError={pickError(
                      policyNumberField.state.meta,
                      submitAttempted
                    )}
                    disabled={isViewMode || blockInteraction}
                  />
                )}
              />
            )}
          />
        )}
      />

      {/* Seção 2 — Dados do Estipulante */}
      <div className='flex flex-col gap-6'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Dados do Estipulante
        </Text>

        <form.AppField
          name='useIndividualDocument'
          children={docTypeField => (
            <form.AppField
              name='documentNumber'
              validators={{
                onChange: ({ value, fieldApi }) =>
                  getCapitalGlobalDocumentError(
                    value,
                    Boolean(
                      fieldApi.form.getFieldValue('useIndividualDocument')
                    )
                  ),
              }}
              children={docNumberField => (
                <form.AppField
                  name='name'
                  validators={{
                    onChange: ({ value }) =>
                      !value || value.trim() === ''
                        ? 'Nome é obrigatório'
                        : undefined,
                  }}
                  children={nameField => (
                    <>
                      <DiretrixDocumentWatcher
                        useIndividualDocument={docTypeField.state.value}
                        documentNumber={docNumberField.state.value}
                        name={nameField.state.value}
                        onNameChange={nameField.handleChange}
                        onAverageAgesChange={handleStipulantAverageAgesChange}
                        disabled={isViewMode || blockInteraction}
                        onStatusChange={handleStipulantDocumentStatusChange}
                      />
                      <StipulantIdentification
                        useIndividualDocument={docTypeField.state.value}
                        onUseIndividualDocumentChange={value => {
                          docTypeField.handleChange(value);
                          // Revalida o documento no novo modo — senão o
                          // "CNPJ inválido" fica preso ao alternar para
                          // CPF/CAEPF (e vice-versa).
                          void form.validateField('documentNumber', 'change');
                        }}
                        documentNumber={docNumberField.state.value}
                        onDocumentNumberChange={value => {
                          if (!docTypeField.state.value) {
                            form.setFieldValue('businessCnae', '');
                            setStipulantCnaeLabel('');
                          }
                          // Trocar o documento descarta o CPF consultado antigo
                          // e libera o modal para abrir de novo na próxima consulta.
                          form.setFieldValue('cpf', '');
                          resetCaepfFor(removeFormatRegisterNumber(value));
                          docNumberField.handleChange(value);
                        }}
                        documentError={
                          pickError(
                            docNumberField.state.meta,
                            submitAttempted
                          ) ??
                          // Documento inválido acusa assim que o número
                          // completa, sem esperar o primeiro "Próximo".
                          getCapitalGlobalDocumentLiveError(
                            docNumberField.state.value,
                            Boolean(docTypeField.state.value)
                          ) ??
                          // Situação cadastral irregular vira erro (vermelho)
                          // abaixo do campo, não helper text cinza.
                          (stipulantDocumentLookup.status === 'inactive-caepf'
                            ? stipulantDocumentLookup.helperText
                            : undefined)
                        }
                        documentHelperText={stipulantDocumentLookup.helperText}
                        name={nameField.state.value}
                        onNameChange={nameField.handleChange}
                        nameError={pickError(
                          nameField.state.meta,
                          submitAttempted
                        )}
                        required
                        disabled={isViewMode || blockInteraction}
                      />
                    </>
                  )}
                />
              )}
            />
          )}
        />

        <form.Subscribe
          selector={state => state.values.useIndividualDocument}
          children={useIndividualDocument => (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
              <form.AppField
                name='businessSegment'
                validators={{
                  onChange: ({ value }) =>
                    !value || value.trim() === ''
                      ? 'Segmento é obrigatório'
                      : undefined,
                }}
                children={field => (
                  <SegmentSelect
                    value={field.state.value}
                    disabled={
                      isViewMode ||
                      blockInteraction ||
                      isSegmentOptionsLoading ||
                      isSegmentOptionsError
                    }
                    options={segmentOptions}
                    selectedLabel={stipulantSegmentLabel}
                    useSelect
                    isLoading={isSegmentOptionsLoading}
                    required
                    error={pickError(field.state.meta, submitAttempted)}
                    onChange={(value, label) => {
                      field.handleChange(value);
                      setStipulantSegmentLabel(label ?? '');
                    }}
                  />
                )}
              />
              <form.AppField
                name='businessCnae'
                validators={{
                  onChange: ({ value }) =>
                    !value || value.trim() === ''
                      ? 'CNAE é obrigatório'
                      : undefined,
                }}
                children={field => (
                  <CnaeField
                    value={field.state.value}
                    onChange={(value, label) => {
                      field.handleChange(value);
                      setStipulantCnaeLabel(label ?? '');
                    }}
                    selectedLabel={stipulantCnaeLabel}
                    options={useIndividualDocument ? cnaeOptions : []}
                    useCombobox={useIndividualDocument}
                    search={stipulantCnaeSearch}
                    onSearchChange={setStipulantCnaeSearch}
                    isLoading={isCnaeOptionsLoading}
                    required
                    error={pickError(field.state.meta, submitAttempted)}
                    disabled={
                      isViewMode ||
                      !useIndividualDocument ||
                      blockInteraction ||
                      isCnaeOptionsError
                    }
                  />
                )}
              />
            </div>
          )}
        />

        <form.Subscribe
          selector={state => state.values.subStipulants.length}
          children={length =>
            length === 0 ? (
              <form.AppField
                name='subStipulants'
                children={field => (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='flex items-center gap-2 self-start'
                    onClick={() =>
                      field.handleChange([
                        ...field.state.value,
                        createSubStipulant(field.state.value),
                      ])
                    }
                    disabled={isViewMode || blockInteraction}
                  >
                    <Plus className='w-5 h-5' />
                    Adicionar Subestipulante
                  </Button>
                )}
              />
            ) : null
          }
        />
      </div>

      {/* Subestipulantes */}
      <form.AppField
        name='subStipulants'
        children={field => (
          <SubStipulantsList
            items={field.state.value}
            onAdd={() =>
              field.handleChange([
                ...field.state.value,
                createSubStipulant(field.state.value),
              ])
            }
            onRemove={id => {
              setSubStipulantDocumentLookups(prev => {
                if (!prev[id]) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
              });
              field.handleChange(
                field.state.value.filter(
                  (s: SubStipulantEntry) => s.id !== id
                )
              );
            }}
            onPatch={(id, partial) => {
              setSubStipulantFieldErrors(prev => {
                if (!prev[id]) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
              });
              const currentSubStipulants =
                (field.form.getFieldValue('subStipulants') as SubStipulantEntry[]) ??
                [];
              field.handleChange(
                currentSubStipulants.map((s: SubStipulantEntry) =>
                  s.id === id ? { ...s, ...partial } : s
                )
              );
            }}
            disabled={isViewMode || blockInteraction}
            segmentOptions={segmentOptions}
            isSegmentOptionsLoading={isSegmentOptionsLoading}
            isSegmentOptionsError={isSegmentOptionsError}
            submitAttempted={submitAttempted}
            fieldErrorsById={subStipulantFieldErrors}
            onDocumentLookupChange={(id, state) =>
              setSubStipulantDocumentLookups(prev => ({
                ...prev,
                [id]: state,
              }))
            }
          />
        )}
      />

      {/* Ações */}
      <div className='flex flex-row justify-between items-center px-6 gap-6 self-stretch'>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2 px-8 py-2 rounded-xl'
          onClick={handleSubmit}
          disabled={isViewMode || !canGoNext || blockInteraction}
        >
          {isPending ? (
            <>
              Processando...
              <Spinner size='sm' colorScheme='gray' />
            </>
          ) : (
            <>
              Próximo
              <ArrowRight className='w-6 h-6' strokeWidth={2} />
            </>
          )}
        </Button>
      </div>

      <SelectCaepfModal
        isOpen={caepfSelection.isOpen}
        onClose={caepfSelection.close}
        onConfirm={caepfSelection.confirm}
        items={caepfSelection.items}
      />
    </div>
  );
}
